import React from 'react';
import connect from './connect';
import Dialog from './dialog.jsx';
import MenuButton from './menu_button.ui';
import {
    createCollection,
    addContentToCollection,
} from '../actions/collections';
import { selectInstructorSections } from '../selectors/section';
import { selectContentForSection } from '../selectors/content';
import PropTypes from 'prop-types';
import R from 'ramda';

class NewCollectionDialogUi extends React.Component {
    static displayName = 'NewCollectionDialog';

    static propTypes = {
        name: PropTypes.string,
        onSave: PropTypes.func.isRequired,
        onClose: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
        sectionId: PropTypes.number,
        sections: PropTypes.any.isRequired,
        sectionContents: PropTypes.array,
    };

    onChange = R.memoize((name, id) => e => {
        this.props.onChange(
            name,
            id,
            name === 'content' ? e.target.checked : e.target.value
        );
    });

    onChangeSection = sectionId => {
        this.props.onChange('sectionId', null, sectionId);
    };

    renderContent() {
        const { sectionContents } = this.props;
        if (!sectionContents) return;

        const asHtml = content => {
            return (
                <li key={content.id}>
                    <input
                        onChange={this.onChange('content', content.id)}
                        type="checkbox"
                        id={'check-' + content.id}
                        defaultValue={'c' + content.id}
                    />
                    <label htmlFor={'check-' + content.id}>
                        {content.title}
                    </label>
                </li>
            );
        };

        return R.compose(
            R.map(asHtml),
            R.sortBy(R.prop('title'))
        )(sectionContents);
    }

    renderSectionMenu() {
        const { sections, sectionId } = this.props;

        const renderSection = section => {
            return (
                <span key={section.id}>
                    {`${section.course_subject} ${
                        section.course_catalog_number
                    } - ${section.course_title} #${section.class_number}`}
                </span>
            );
        };

        return (
            <MenuButton
                items={sections}
                value={sectionId}
                onSelection={this.onChangeSection}
                renderItem={renderSection}
            >
                Select a section...
            </MenuButton>
        );
    }
    render() {
        const buttons = [];
        buttons.push({
            label: 'Cancel',
            dismiss: true,
        });

        buttons.push({
            label: 'Save',
            onClick: this.props.onSave,
            primary: true,
        });

        return (
            <Dialog
                buttons={buttons}
                closeButton
                onClose={this.props.onClose}
                title="Add A Collection"
            >
                <div data-view-name="ManageContentView">
                    <p className="aural">
                        All fields are required unless otherwise noted.
                    </p>
                    <fieldset>
                        <legend className="aural">Collection Name:</legend>
                        <label htmlFor="collectionTitle">
                            Collection Title
                        </label>
                        <input
                            value={this.props.name}
                            onChange={this.onChange('name')}
                            name="collection_title"
                            id="collectionTitle"
                            className="input-collection-title"
                            required="required"
                            type="text"
                            aria-required="true"
                        />
                    </fieldset>

                    <fieldset
                        aria-live="polite"
                        aria-describedby="ariaCopyFromHeader"
                    >
                        <legend className="aural">Course Selection</legend>
                        <ul>
                            <li>
                                <label>
                                    <span className="aural">
                                        Required: Check at least one{' '}
                                    </span>
                                    Section
                                </label>
                                <div>{this.renderSectionMenu()}</div>
                            </li>
                        </ul>
                    </fieldset>

                    <fieldset className="group">
                        <legend>
                            Choose materials to add to this collection
                        </legend>
                        <fieldset aria-live="polite">
                            <legend>
                                <span className="aural">
                                    Check at least one.
                                </span>
                            </legend>
                            <div className="js-material-selections material-selections">
                                <div data-view-name="CheckboxesView">
                                    <ul>{this.renderContent()}</ul>
                                </div>
                            </div>
                        </fieldset>
                    </fieldset>
                </div>
            </Dialog>
        );
    }
}

class CreateCollectionDialog extends React.Component {
    static displayName = 'CreateCollectionDialog(State)';

    static propTypes = {
        createCollection: PropTypes.func.isRequired,
        addContentToCollection: PropTypes.func.isRequired,
        sectionId: PropTypes.number,
        sections: PropTypes.any.isRequired,
        stateHack: PropTypes.object.isRequired,
        onClose: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        let sectionId = this.props.sectionId;
        if (!sectionId && this.props.sections[0]) {
            sectionId = this.props.sections[0].id;
        }
        this.state = {
            ...this.findSectionState(sectionId),
            name: '',
        };
    }

    onChange = (name, id, value) => {
        if (name === 'sectionId') {
            this.setState(this.findSectionState(value));
        } else if (name === 'name') {
            this.setState({ name: value });
        } else if (name === 'content') {
            this.setState({
                selectedSectionContents: value
                    ? R.uniq([...this.state.selectedSectionContents, id])
                    : R.without([id], this.state.selectedSectionContents),
            });
        }
    };

    onSave = () => {
        if (this.state.name.length > 0) {
            this.props
                .createCollection(this.state.sectionId, this.state.name)
                .then(this.addItems)
                .then(this.props.onClose);
        }
    };

    addItems = collection => {
        return this.state.selectedSectionContents.reduce(
            (promise, content_id) => {
                const content = this.state.sectionContents.find(
                    c => c.id === content_id
                );
                return promise.then(() => {
                    this.props.addContentToCollection(content, collection);
                });
            },
            Promise.resolve({})
        );
    };

    findSectionState = sectionId => {
        sectionId = +sectionId;
        if (!sectionId) return {};
        const section = this.props.sections.find(s => s.id === sectionId);
        const sectionContents = !section
            ? null
            : selectContentForSection(this.props.stateHack, section);

        return {
            sectionId,
            section,
            sectionContents,
            selectedSectionContents: [],
        };
    };

    render() {
        const sectionId =
            this.state.sectionId || R.head(this.props.sections).id;

        return (
            <NewCollectionDialogUi
                onClose={this.props.onClose}
                onChange={this.onChange}
                onSave={this.onSave}
                sectionId={sectionId}
                sections={this.props.sections}
                sectionContents={this.state.sectionContents}
                selectedSectionContents={this.state.selectedSectionContents}
                name={this.state.name}
            />
        );
    }
}

const mapStateToProps = (state, props) => {
    return {
        sections: selectInstructorSections(state),
        rootState: state,
        stateHack: state,
    };
};

export default connect(
    mapStateToProps,
    {
        createCollection,
        addContentToCollection,
    }
)(CreateCollectionDialog);



// WEBPACK FOOTER //
// ./src/components/new_collection_dialog.jsx